import type { HistorianStatus } from '@axilon-apps/domains-ontology-ui-state'

/** Binary byte size: 1536 -> "1.5 KiB". */
export function formatHistorianBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB']
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exp
  return `${exp === 0 ? value : value.toFixed(1)} ${units[exp]}`
}

/** Updates/sec with precision that stays readable from 0.01/s to 100k/s. */
export function formatUpdatesPerSecond(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return '0 updates/s'
  const digits = rate >= 100 ? 0 : rate >= 1 ? 1 : 2
  return `${rate.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })} updates/s`
}

export interface HistorianStatusSummary {
  throughput: string
  fullness: string
}

/**
 * Renders the diagnostic as two short lines. Fullness never shows a percent:
 * no storage capacity is configured anywhere the proxy can read, so it is
 * used bytes plus the retention that bounds growth.
 */
export function summarizeHistorianStatus(status: HistorianStatus): HistorianStatusSummary {
  if (!status.configured) {
    return {
      throughput: 'Historian not configured',
      fullness: 'Tag history is not stored in a dedicated historian database'
    }
  }
  const { throughput, storage } = status
  const windowMinutes = Math.round(throughput.windowSeconds / 60)
  const retention =
    storage.retentionDays === null
      ? storage.timescale
        ? 'no retention policy (kept forever)'
        : 'no retention (plain Postgres)'
      : `${Number(storage.retentionDays.toFixed(1))}-day retention`
  const used =
    storage.readingsBytes === null
      ? `${formatHistorianBytes(storage.databaseBytes)} used`
      : `${formatHistorianBytes(storage.databaseBytes)} used (readings ${formatHistorianBytes(storage.readingsBytes)})`
  return {
    throughput: `${formatUpdatesPerSecond(throughput.updatesPerSecond)} · ${windowMinutes}-min avg`,
    fullness: storage.capacityConfigured
      ? `${used} · ${retention}`
      : `${used} · ${retention} · no capacity configured`
  }
}
